import {FC, useCallback, useEffect, useState} from 'react';

import InterWindowController from './InterWindowController';

declare global {
  interface Window {
    graphExplorerControllerWindow: Window;
  }
}

type GraphExplorerNewWindowProps = {
  url?: string;
  onOpen?: (window: Window) => void;
  onClose?: () => void;
  saveQuery?: (query: string) => void;
  defaultQuery?: string;
  explorerProps?: object;
};

const GraphExplorerNewWindow: FC<GraphExplorerNewWindowProps> = ({
  url = '#/platform/developer/graph-explorer',
  onOpen,
  onClose,
  saveQuery,
  defaultQuery,
  explorerProps,
}) => {
  const [explorerWindow, setExplorerWindow] = useState<Window | null>(null);

  const onUnload = useCallback(() => {
    onClose?.();
    setExplorerWindow(null);
  }, [onClose]);

  useEffect(() => {
    const explorerWindow = window.open(url);
    if (explorerWindow !== null) {
      explorerWindow.graphExplorerControllerWindow = window;
      explorerWindow.focus();
      explorerWindow.addEventListener('load', () => {
        explorerWindow.addEventListener('beforeunload', onUnload);
        onOpen?.(explorerWindow);
        window.addEventListener('beforeunload', onUnload);
        setExplorerWindow(explorerWindow);
      });
      return () => {
        explorerWindow?.close();
        window.removeEventListener('beforeunload', onUnload);
      };
    }
  }, [onOpen, onUnload, url]);

  const handleSaveQuery = (query) => {
    saveQuery?.(query);
    onUnload();
  };

  return (
    explorerWindow &&
      <InterWindowController
        defaultQuery={defaultQuery}
        saveQuery={handleSaveQuery}
        window={explorerWindow}
        {...explorerProps}
      />
  );
};

export default GraphExplorerNewWindow;
